import * as React from 'react'

import {
  Box,
  SearchField,
  Skeleton,
  type SortDirection,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import { filterFields } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import GroupFilters from 'modules/core/components/GroupFilters'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import { useEmployeeGroups } from 'modules/core/components/MultiFilters/hooks/useEmployeeGroups'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'

import useGroupFieldsByPermissions from '../useGroupFieldsByPermissions'

type EmployeeMultiFiltersProps = React.ComponentProps<typeof EmployeeMultiFilters>

interface Props {
  filtersForm: FiltersForm
  /**
   * Use a more minimal representation for a modern look.
   */
  dense?: boolean
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  search: string
  onApplyGroupFilter: () => void
  onChangeGroupFilter: (filter: string) => void
  onChangeGroupOrder: (order: SortDirection) => void
  countGroupFilters: number
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: EmployeeMultiFiltersProps['onApplyFilters']
  onCancel: () => void
  onFiltersLoaded: () => void
  localSavedDefaultGrouping: string
  filterKey?: string
  filterDependencies?: string[]
}

const SearchBar = ({
  dense,
  onSearch,
  search,
  onApplyGroupFilter,
  onChangeGroupFilter,
  onChangeGroupOrder,
  countGroupFilters,
  filters,
  logicOperator,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  onFiltersLoaded,
  localSavedDefaultGrouping,
  filterKey,
  filterDependencies,
}: Props) => {
  const [groupFilterFields, groupOrderFields] = useGroupFieldsByPermissions()
  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading: loadingEmployeeGroups, employeeGroups } = useEmployeeGroups()
  const { loading: loadingFields } = useAsyncData(async () => {
    const newFields = await filterFields(employeeGroups)

    setFields(newFields)
    onFiltersLoaded()
  }, [employeeGroups.length])

  const loading = loadingEmployeeGroups || loadingFields

  return (
    <Box width="full">
      <Box gap="s8" flexNoShrink flexDirection="row" alignItems="center">
        {loading ? (
          <>
            <Skeleton width="s96" height="s32" />
            <Skeleton width="s96" height="s32" />
          </>
        ) : (
          <>
            <EmployeeMultiFilters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              dense={dense}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={onApplyFilters}
              onCancel={onCancel}
              showDefaultFilters
              filterKey={filterKey}
              filterDependencies={filterDependencies}
            />
            <GroupFilters
              dense={dense}
              label={i18n.t('filters.group_filters.group')}
              onApplyGroupFilter={onApplyGroupFilter}
              onChangeGroupFilter={onChangeGroupFilter}
              onChangeGroupOrder={onChangeGroupOrder}
              groupFilterFields={groupFilterFields}
              groupOrderFields={groupOrderFields}
              countGroupFilters={countGroupFilters}
              savedDefaultGroupingFilters={localSavedDefaultGrouping}
            />
          </>
        )}

        {dense && <Box flexGrow />}
        <Box width={dense ? undefined : 'full'}>
          <SearchField
            variant={dense ? 'regular' : 'big'}
            onSearch={onSearch}
            search={search}
            placeholder={i18n.t('common.search')}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(SearchBar)
